/**
 * Feature flag registry — the single source of truth (W0.1).
 *
 * WHY THIS LIVES HERE
 * -------------------
 * Every flag key, and its compiled default, is declared once in this file. The
 * key union is DERIVED from the defaults object, so a flag cannot exist in the
 * type system without a default, and cannot be given a default without entering
 * the type system. That is the property the three per-app `feature-flags.ts`
 * files never had: 70 literals across three apps with no shared type, so a key
 * could be renamed in one and silently survive in the others.
 *
 * `@tonnie/foundation` rather than `@tonnie/types` because these are runtime
 * values and that package declares itself type-only. `plan-gating` next door is
 * the precedent: shared domain rules both the API and the clients execute.
 *
 * THE CONTRACT
 * ------------
 * Every flag defaults to `false`. With a flag off, the platform behaves exactly
 * as it does without the feature that flag ramps — that is the rule a ramp
 * rests on, and it is only meaningful if "off" is what a client falls back to
 * when the endpoint is unreachable, unparseable, or has never heard of the key.
 * A flag that defaults to `true` breaks that rule silently, so the type below
 * forbids it.
 *
 * ADDING A FLAG: add it here with `false`, and nowhere else. The server's
 * resolver, the SDK hook and every app pick it up from this object.
 *
 * REMOVING A FLAG: once the feature is permanent, delete the entry, every code
 * branch that read it (the off-path included), and the values stored for it —
 * a migration strips them from `platform_configs` and the user layer, as 0168,
 * 0171 and 0178 did. A flag left behind after its ramp is dead config that
 * still costs a resolution branch.
 *
 * THE MACHINERY OUTLIVES ITS FLAGS. With nothing ramping, the registry is empty
 * and every client reads an empty set — the endpoint, the resolver, the admin
 * write and the SDK hook stay, because the next ramp needs all of them on day
 * one. They are rules over a registry (`createFlagRegistry`), so their tests
 * run against a fixture registry rather than waiting for a real flag.
 */

/**
 * Compiled defaults. Every value is `false` — see the contract above. The
 * `satisfies` clause is what enforces it: a `true` here fails the build rather
 * than shipping a feature that is on before anyone flips it.
 *
 * `MOBILE_HOME_V2` (F46) retired 27 Sep 2026 with Home V2 — the phone's Home
 * is the web's scenario control (Citizen Mobile · D-M6).
 */
export const FEATURE_FLAG_DEFAULTS = {
  /**
   * The Family add-on: the citizen's paid `family_addon` plan and Family
   * Program, and a company's family access on a sponsorship. Unbuilt — the
   * family routes do not exist — so it is out of production until it is
   * (decided 28 Sep 2026). Off: the plan is neither listed nor sold, the
   * sponsorship family toggle is a 404, and no portal shows any of it.
   * Cancelling an add-on someone already holds stays open on purpose.
   */
  FAMILY_ADDON: false,
  /**
   * Career: opportunities, saved roles, expressions of interest and the CV
   * builder. Out of production until it is built on real matching (decided
   * 28 Sep 2026). Off: every `/career` route is a 404, the Companion has no
   * career tools, and no portal shows it.
   */
  CAREER: false,
  /**
   * Certified invoicing (docs/BILLING-CERTIFIED-INVOICING-JASMIN.md): every
   * paid platform sale gets an invoice-receipt issued in Jasmin, and a
   * checkout opens only with a complete billing profile. Off until the
   * controlled start (§8); a payment made while it is off is issued later by
   * the retroactive command.
   */
  FISCAL_INVOICING_ENABLED: false,
  /**
   * InOut, the seventh hero (docs/01-platform/04-heroes/inout): the person's
   * record of residence absences, their clocks and their legal notices. Off
   * until it is released with Ei!: every `/inout` route is a 404 and no InOut
   * notice is sent. Ramped: the E2E suite proves it on.
   */
  INOUT_ENABLED: false,
  /**
   * Tonnie Languages Academy — the QultureUp World that opens Comuniverse's
   * language courses (docs/TONNIE-COMMUNICATION-PROFICIENCY-ASSESSMENT.md,
   * revision 4). Off: every `/heroes/qultureup/academy` route is a 404, and
   * the partner routes know no code and no learner. Production stays off until
   * phase 2 (Comuniverse's suspend/delete interface and the signed data
   * agreement). Ramped: the E2E suite proves it on.
   */
  LANGUAGE_ACADEMY_ENABLED: false,
} satisfies Record<string, false>;

/** Every flag key. Derived — never hand-maintained beside the defaults. */
export type FeatureFlagKey = keyof typeof FEATURE_FLAG_DEFAULTS;

/**
 * What a flag is for. Both kinds default off; they differ in what a full E2E
 * run must prove.
 *
 * - `ramp`   — a new behaviour being rolled out. The E2E seed turns it ON
 *              globally, so the suite proves the behaviour the flag enables;
 *              a block that proves the off position sets it at its own layer.
 * - `parked` — a feature held out of production until it is built. The E2E
 *              suite runs it OFF, exactly as production does, and a block
 *              that proves the parked feature turns it on for its own tenant
 *              or user.
 */
export type FeatureFlagStage = 'ramp' | 'parked';

/** Every flag declares its stage — a `Record` over the keys, so none can be missed. */
export const FEATURE_FLAG_STAGE: Readonly<Record<FeatureFlagKey, FeatureFlagStage>> = {
  FAMILY_ADDON: 'parked',
  CAREER: 'parked',
  FISCAL_INVOICING_ENABLED: 'ramp',
  INOUT_ENABLED: 'ramp',
  LANGUAGE_ACADEMY_ENABLED: 'ramp',
};

/** A fully resolved set: every key present, every value a boolean. */
export type ResolvedFlags = Record<FeatureFlagKey, boolean>;

/** The flag endpoint's payload. Resolved booleans only — never rules. */
export interface FeatureFlagsPayload {
  /** Bumped when the resolution shape changes, not when a value flips. */
  version: number;
  resolvedAt: string;
  scope: {
    tenantId: string | null;
    userId: string | null;
  };
  flags: ResolvedFlags;
}

/** Payload shape version. Bump only on a breaking change to the envelope. */
export const FEATURE_FLAGS_PAYLOAD_VERSION = 1;

/**
 * The rules over one set of defaults: which keys exist, how an untrusted stored
 * layer is narrowed to them, and how layers resolve. The platform's registry is
 * `createFlagRegistry(FEATURE_FLAG_DEFAULTS)`; a test builds its own over
 * fixture keys and exercises exactly the same rules.
 */
export interface FlagRegistry<K extends string> {
  readonly defaults: Readonly<Record<K, false>>;
  /** Is this arbitrary string a flag we know about? */
  isKey(key: string): key is K;
  /**
   * Narrow an untrusted record to known keys and real booleans.
   *
   * Used on every stored layer — the global row, a tenant row, a user's
   * settings blob — because all three are operator-editable JSON that can
   * drift from the code. Unknown keys are dropped (a retired flag must not
   * resurrect via a stale row) and non-booleans are ignored rather than
   * coerced: `"false"` is a string, and coercing it would turn a flag ON, which
   * is the one direction that must never happen by accident.
   */
  sanitize(input: unknown): Partial<Record<K, boolean>>;
  /**
   * Apply layers in ascending precedence, then force off anything the kill
   * switch names. Pure, so the precedence is testable without a database.
   *
   * The kill switch is applied LAST and can only turn things off — it is the
   * lever for an incident, and an incident lever that could also turn
   * features on is not a safety control.
   */
  resolve(
    layers: Array<Partial<Record<K, boolean>> | undefined>,
    forceOff?: readonly string[]
  ): Record<K, boolean>;
}

export function createFlagRegistry<K extends string>(defaults: Record<K, false>): FlagRegistry<K> {
  const keys = Object.keys(defaults) as K[];
  const isKey = (key: string): key is K => Object.prototype.hasOwnProperty.call(defaults, key);
  return {
    defaults,
    isKey,
    sanitize(input) {
      if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
      const out: Partial<Record<K, boolean>> = {};
      for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
        if (typeof value === 'boolean' && isKey(key)) out[key] = value;
      }
      return out;
    },
    resolve(layers, forceOff = []) {
      const resolved = { ...defaults } as Record<K, boolean>;
      for (const layer of layers) {
        if (!layer) continue;
        for (const key of keys) {
          const value = layer[key];
          if (typeof value === 'boolean') resolved[key] = value;
        }
      }
      for (const raw of forceOff) {
        const key = raw.trim();
        if (isKey(key)) resolved[key] = false;
      }
      return resolved;
    },
  };
}

/** The platform's registry — the one the endpoint, the admin write and the SDK read. */
export const FEATURE_FLAGS: FlagRegistry<FeatureFlagKey> =
  createFlagRegistry(FEATURE_FLAG_DEFAULTS);

/**
 * Parse `FLAGS_FORCE_OFF` (comma-separated). Must be called PER REQUEST, never
 * snapshotted in a constructor — `CoPilotCapabilitiesService` is the standing
 * example of a config read frozen at boot, which makes the value unchangeable
 * without a restart. A kill switch that needs a restart is not a kill switch.
 */
export function parseForceOff(raw: string | undefined | null): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}
